import React from 'react';
import { createPortal } from 'react-dom';
import type { OrderStageGroup } from '../../data/orderStageGroups';
import { formatOrderStatusLabel } from '../../lib/orderStages';
import type { OrderStageLookup } from '../../lib/orderStages';

const DISPLAY_ORDER: string[] = ['quote', 'confirmed', 'in_progress', 'ready_to_invoice', 'done'];

type StageGroupItem = { type: 'group'; group_key: string; label: string; statuses: string[] };
type StageStandaloneItem = { type: 'standalone'; status: string; label: string };
type StageFilterItem = StageGroupItem | StageStandaloneItem;

export function buildOrderedStageItems(
  stageGroups: OrderStageGroup[],
  standaloneStatuses: string[],
  orderStageLookup: OrderStageLookup,
): StageFilterItem[] {
  const groupMap = new Map(stageGroups.map((g) => [g.group_key, g]));
  const standaloneSet = new Set(standaloneStatuses);
  const getLabel = (status: string) => orderStageLookup[status]?.label ?? formatOrderStatusLabel(status);
  const items: StageFilterItem[] = [];
  const seenStandalones = new Set<string>();

  for (const key of DISPLAY_ORDER) {
    const group = groupMap.get(key);
    if (group && group.statuses.length > 0) {
      items.push({ type: 'group', group_key: group.group_key, label: group.label, statuses: group.statuses });
    } else if (standaloneSet.has(key)) {
      seenStandalones.add(key);
      items.push({ type: 'standalone', status: key, label: getLabel(key) });
    }
  }

  for (const group of stageGroups) {
    if (!DISPLAY_ORDER.includes(group.group_key) && group.statuses.length > 0) {
      items.push({ type: 'group', group_key: group.group_key, label: group.label, statuses: group.statuses });
    }
  }

  for (const status of standaloneStatuses) {
    if (!seenStandalones.has(status)) {
      items.push({ type: 'standalone', status, label: getLabel(status) });
    }
  }

  return items;
}

function uniqueOrdered(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

function getStatusLabel(status: string, lookup: OrderStageLookup): string {
  return lookup[status]?.label ?? formatOrderStatusLabel(status);
}

function setInputIndeterminate(element: HTMLInputElement | null, indeterminate: boolean) {
  if (element) element.indeterminate = indeterminate;
}

type StageFilterToolbarProps = {
  selectedStatuses: Set<string>;
  onSelectedStatusesChange: (statuses: string[]) => void;
  stageGroups: OrderStageGroup[];
  standaloneStatuses: string[];
  orderStageLookup: OrderStageLookup;
  fallbackStatuses?: string[];
  defaultSelectedStatuses?: string[];
  compact?: boolean;
  /** Trigger label and panel heading. Libraries that filter something other than
   *  order stages (the warehouse filters transfer statuses) relabel it. */
  label?: string;
  pluralNoun?: string;
  allLabel?: string;
  /** Name for the "restore the default selection" shortcut; `null` hides it,
   *  which is right when the default already is "everything". */
  defaultSelectionLabel?: string | null;
};

export default function StageFilterToolbar({
  selectedStatuses,
  onSelectedStatusesChange,
  stageGroups,
  standaloneStatuses,
  orderStageLookup,
  fallbackStatuses = [],
  defaultSelectedStatuses = [],
  compact,
  label = 'Stage',
  pluralNoun = 'stages',
  allLabel = 'All Stages',
  defaultSelectionLabel = 'Pipeline',
}: StageFilterToolbarProps) {
  const [open, setOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const [expandedGroupKeys, setExpandedGroupKeys] = React.useState<Set<string>>(() => new Set());
  const rootRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const groupedItems = React.useMemo(
    () => buildOrderedStageItems(stageGroups, standaloneStatuses, orderStageLookup),
    [stageGroups, standaloneStatuses, orderStageLookup],
  );
  const useFallback = groupedItems.length === 0 && fallbackStatuses.length > 0;

  const items = React.useMemo<StageFilterItem[]>(() => {
    if (!useFallback) return groupedItems;
    return fallbackStatuses.map((status) => ({
      type: 'standalone',
      status,
      label: getStatusLabel(status, orderStageLookup),
    }));
  }, [fallbackStatuses, groupedItems, orderStageLookup, useFallback]);

  const selectableStatuses = React.useMemo(() => {
    const statuses: string[] = [];
    for (const item of items) {
      if (item.type === 'group') {
        statuses.push(...item.statuses);
      } else {
        statuses.push(item.status);
      }
    }
    for (const status of selectedStatuses) {
      if (!statuses.includes(status)) statuses.push(status);
    }
    return uniqueOrdered(statuses);
  }, [items, selectedStatuses]);

  const selectableStatusSet = React.useMemo(() => new Set(selectableStatuses), [selectableStatuses]);
  const selectedKnownStatuses = React.useMemo(
    () => new Set(selectableStatuses.filter((status) => selectedStatuses.has(status))),
    [selectableStatuses, selectedStatuses],
  );

  const selectedCount = selectedKnownStatuses.size;
  const totalCount = selectableStatuses.length;
  const defaultStatusSet = React.useMemo(() => new Set(defaultSelectedStatuses), [defaultSelectedStatuses]);
  const isDefaultSelection = totalCount > 0
    && selectedCount === defaultSelectedStatuses.filter((status) => selectableStatusSet.has(status)).length
    && selectableStatuses.every((status) => selectedKnownStatuses.has(status) === defaultStatusSet.has(status));
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const summaryText = React.useMemo(() => {
    if (totalCount === 0) return 'Loading';
    if (selectedCount === 0) return `No ${pluralNoun}`;
    if (allSelected) return allLabel;
    if (isDefaultSelection && defaultSelectionLabel) return defaultSelectionLabel;
    if (selectedCount === 1) {
      const onlyStatus = selectableStatuses.find((status) => selectedKnownStatuses.has(status));
      return onlyStatus ? getStatusLabel(onlyStatus, orderStageLookup) : '1 selected';
    }
    return `${selectedCount} selected`;
  }, [allLabel, allSelected, defaultSelectionLabel, isDefaultSelection, orderStageLookup, pluralNoun, selectableStatuses, selectedCount, selectedKnownStatuses, totalCount]);

  const orderSelection = React.useCallback(
    (statuses: Iterable<string>) => {
      const next = new Set(statuses);
      const ordered = selectableStatuses.filter((status) => next.has(status));
      for (const status of next) {
        if (!selectableStatusSet.has(status)) ordered.push(status);
      }
      return ordered;
    },
    [selectableStatuses, selectableStatusSet],
  );

  const replaceSelection = React.useCallback(
    (statuses: Iterable<string>) => {
      onSelectedStatusesChange(orderSelection(statuses));
    },
    [onSelectedStatusesChange, orderSelection],
  );

  const updatePanelPosition = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const margin = 12;
    const gap = 6;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const panelWidth = Math.max(0, Math.min(520, viewportWidth - margin * 2));
    const minWidth = Math.max(0, Math.min(360, viewportWidth - margin * 2));
    const left = Math.max(margin, Math.min(rect.left, viewportWidth - panelWidth - margin));
    const belowSpace = viewportHeight - rect.bottom - margin;
    const aboveSpace = rect.top - margin;
    const openAbove = belowSpace < 240 && aboveSpace > belowSpace;
    const maxHeight = Math.max(180, Math.min(560, (openAbove ? aboveSpace : belowSpace) - gap));
    const top = openAbove
      ? Math.max(margin, rect.top - maxHeight - gap)
      : Math.min(rect.bottom + gap, viewportHeight - margin);
    setPanelStyle({
      position: 'fixed',
      top,
      left,
      width: panelWidth,
      minWidth,
      maxHeight,
      zIndex: 5000,
    });
  }, []);

  const toggleStatus = React.useCallback(
    (status: string, checked: boolean) => {
      const next = new Set(selectedKnownStatuses);
      if (checked) next.add(status);
      else next.delete(status);
      replaceSelection(next);
    },
    [replaceSelection, selectedKnownStatuses],
  );

  const setGroupSelection = React.useCallback(
    (statuses: string[], checked: boolean) => {
      const next = new Set(selectedKnownStatuses);
      for (const status of statuses) {
        if (checked) next.add(status);
        else next.delete(status);
      }
      replaceSelection(next);
    },
    [replaceSelection, selectedKnownStatuses],
  );

  const toggleExpanded = React.useCallback((groupKey: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      if (panelRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  const renderStandalone = (item: StageStandaloneItem) => {
    const checked = selectedKnownStatuses.has(item.status);
    return (
      <label key={item.status} className="dropdown-menu__item library-toolbar__stage-option">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => toggleStatus(item.status, event.target.checked)}
        />
        <span>{item.label}</span>
      </label>
    );
  };

  const renderGroup = (item: StageGroupItem) => {
    const groupStatuses = item.statuses.filter((status) => selectableStatusSet.has(status));
    const groupSelectedCount = groupStatuses.filter((status) => selectedKnownStatuses.has(status)).length;
    const groupAllSelected = groupStatuses.length > 0 && groupSelectedCount === groupStatuses.length;
    const groupNoneSelected = groupSelectedCount === 0;
    const isExpanded = expandedGroupKeys.has(item.group_key);
    const state = groupAllSelected ? 'all' : groupNoneSelected ? 'none' : 'partial';

    return (
      <div key={item.group_key} className="library-toolbar__stage-group" data-state={state}>
        <div className="library-toolbar__stage-group-row">
          <input
            type="checkbox"
            checked={groupAllSelected}
            ref={(element) => setInputIndeterminate(element, !groupAllSelected && !groupNoneSelected)}
            onChange={(event) => setGroupSelection(groupStatuses, event.target.checked)}
            aria-label={`${item.label} stages`}
          />
          <button
            type="button"
            className="library-toolbar__stage-group-main"
            onClick={() => toggleExpanded(item.group_key)}
            aria-expanded={isExpanded}
          >
            <span className="library-toolbar__stage-group-title">{item.label}</span>
            <span className="library-toolbar__stage-group-count">{groupSelectedCount}/{groupStatuses.length}</span>
          </button>
          <button
            type="button"
            className="library-toolbar__stage-group-expand"
            onClick={() => toggleExpanded(item.group_key)}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label}`}
            aria-expanded={isExpanded}
          />
        </div>
        {isExpanded && (
          <div className="library-toolbar__stage-options">
            {groupStatuses.map((status) => (
              <label key={status} className="dropdown-menu__item library-toolbar__stage-option">
                <input
                  type="checkbox"
                  checked={selectedKnownStatuses.has(status)}
                  onChange={(event) => toggleStatus(status, event.target.checked)}
                />
                <span>{getStatusLabel(status, orderStageLookup)}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const stagePanel = open ? (
    <div
      ref={panelRef}
      className="dropdown-menu library-toolbar__stage-panel"
      role="dialog"
      aria-label={`${label} filter`}
      style={panelStyle}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        // Closing the panel uses this Escape; a surrounding filter sheet stays open.
        event.preventDefault();
        setOpen(false);
      }}
    >
      <div className="dropdown-menu__header library-toolbar__stage-panel-header">
        <div>
          <div className="dropdown-menu__title">{label} filter</div>
          <div className="dropdown-menu__subtitle">{selectedCount} of {totalCount} {pluralNoun} shown</div>
        </div>
        <button type="button" className="library-toolbar__stage-panel-close" onClick={() => setOpen(false)} aria-label={`Close ${label.toLowerCase()} filter`}>
          x
        </button>
      </div>

      <div className="library-toolbar__stage-actions">
        {defaultSelectedStatuses.length > 0 && defaultSelectionLabel && (
          <button
            type="button"
            className={`library-toolbar__stage-action${isDefaultSelection ? ' is-active' : ''}`}
            onClick={() => replaceSelection(defaultSelectedStatuses)}
          >
            {defaultSelectionLabel}
          </button>
        )}
        <button
          type="button"
          className={`library-toolbar__stage-action${allSelected ? ' is-active' : ''}`}
          onClick={() => replaceSelection(selectableStatuses)}
        >
          {allLabel}
        </button>
        <button
          type="button"
          className={`library-toolbar__stage-action${selectedCount === 0 ? ' is-active' : ''}`}
          onClick={() => replaceSelection([])}
        >
          Deselect All
        </button>
      </div>

      <div className="dropdown-menu__section library-toolbar__stage-list">
        {items.length > 0 ? (
          items.map((item) => (item.type === 'group' ? renderGroup(item) : renderStandalone(item)))
        ) : (
          <div className="library-toolbar__stage-empty">No stages available</div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`library-toolbar__stage-filter${compact ? ' library-toolbar__stage-filter--compact' : ''}`}
    >
      <button
        type="button"
        className={`library-toolbar__stage-trigger dropdown-button${isDefaultSelection ? '' : ' is-active'}`}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !open) return;
          event.preventDefault();
          setOpen(false);
        }}
        aria-expanded={open}
        aria-label={`Filter by ${label.toLowerCase()}: ${summaryText}`}
        disabled={totalCount === 0}
      >
        <span className="library-toolbar__stage-trigger-label">{label}</span>
        <span className="library-toolbar__stage-trigger-summary">{summaryText}</span>
        {totalCount > 0 && !allSelected && (
          <span className="library-toolbar__stage-trigger-count">{selectedCount}/{totalCount}</span>
        )}
      </button>

      {stagePanel && (typeof document !== 'undefined' ? createPortal(stagePanel, document.body) : stagePanel)}
    </div>
  );
}
